import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Shield, Factory, Microscope, TrendingUp } from 'lucide-react';
import { heroImages, rdImages } from '../data/mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                About Us
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              Building the Future of <span className="font-medium text-brand-teal">Cosmetic Innovation</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Founded in 2024 by professionals with over a decade of experience in skincare and haircare research and manufacturing.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Our Story
              </h2>
              <div className="space-y-6 text-base text-muted-foreground leading-relaxed">
                <p>
                  V Cosmetic Crafters Private Limited was founded in 2024 by a team of professionals with more than 10 years of experience in the research and manufacturing of skincare and haircare products. Our team has closely worked with 150+ brands, supporting them across formulation development, innovation, and large-scale manufacturing.
                </p>
                <p>
                  We started V Cosmetic Crafters with a clear purpose—to support passionate brand founders who face challenges such as high minimum order quantities (MOQs) and limited opportunities for true innovation. We believe brand building should go beyond relabelling or white labelling and should be driven by meaningful formulation science and customization.
                </p>
                <p>
                  We partner with brands to help them start and grow in the right direction. Instead of offering what is readily available, we focus on understanding what a brand truly needs. We encourage open discussions, explore multiple possibilities, and work collaboratively to develop practical and effective solutions.
                </p>
              </div>
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={heroImages.primary}
                alt="Our laboratory"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Our <span className="font-medium text-brand-teal">Core Values</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Principles that guide every partnership and formulation we develop
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Quality & Compliance",
                description: "We strictly follow the norms and guidelines laid down by the Indian FDA and the Drugs and Cosmetics Act, ensuring every product meets regulatory and quality standards."
              },
              {
                icon: Users,
                title: "True Partnership",
                description: "We believe in teamwork, transparency, and long-term partnerships. Our team remains accessible and responsive at every stage, providing consistent support whenever our partners need us."
              },
              {
                icon: Microscope,
                title: "Scientific Excellence",
                description: "We specialise in advanced skin and haircare R&D, combining scientific excellence with smart cost optimisation to deliver affordable luxury products without compromising on quality."
              },
              {
                icon: Award,
                title: "Meaningful Innovation",
                description: "Brand building should be driven by meaningful formulation science and customization, not just relabelling or white labelling."
              },
              {
                icon: TrendingUp,
                title: "Growth-Oriented",
                description: "We partner with brands to help them start and grow in the right direction, exploring multiple possibilities and developing practical solutions."
              },
              {
                icon: Factory,
                title: "Flexible Manufacturing",
                description: "Our two fully licensed manufacturing facilities support both emerging startups and established brands with flexible capacities for seamless scaling."
              }
            ].map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  className="bg-background p-8 hover-lift"
                >
                  <div className="w-12 h-12 bg-brand-teal/10 flex items-center justify-center mb-6">
                    <IconComponent className="text-brand-teal" size={24} />
                  </div>
                  <h3 className="text-xl font-medium mb-3">{value.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="aspect-[4/5] overflow-hidden lg:order-2">
              <img
                src={rdImages.testing}
                alt="Our approach"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="lg:order-1">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Our <span className="font-medium text-brand-teal">Approach</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-brand-teal font-semibold">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Understanding Your Needs</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Instead of offering what is readily available, we focus on understanding what a brand truly needs through open discussions and collaborative exploration.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-brand-teal font-semibold">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Exploring Possibilities</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      We encourage open discussions and explore multiple possibilities to find the best solution for your brand's unique requirements.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-brand-teal font-semibold">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Developing Solutions</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      We work collaboratively to develop practical and effective solutions that balance innovation, quality, and commercial viability.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-brand-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-brand-teal font-semibold">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">Continuous Support</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Our team remains accessible and responsive at every stage, providing consistent support whenever our partners need us.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Facilities */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Our <span className="font-medium text-brand-teal">Manufacturing Facilities</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Two fully licensed manufacturing facilities designed to support both emerging startups and well-established brands, providing flexible capacities that enable seamless scaling without compromising on quality or regulatory compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-background p-8">
              <h3 className="text-xl font-medium mb-4">Flexible Capacity</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Support for both small batch production for startups and large-scale manufacturing for established brands.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  Low MOQ for emerging brands
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  Scalable production lines
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  Quick turnaround times
                </li>
              </ul>
            </div>

            <div className="bg-background p-8">
              <h3 className="text-xl font-medium mb-4">Full Compliance</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                Fully licensed facilities adhering to Indian FDA and Drugs and Cosmetics Act guidelines.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  GMP certified operations
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  Quality assurance protocols
                </li>
                <li className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-brand-teal mt-1">•</span>
                  Regular audits and inspections
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-foreground text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Our Impact
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { metric: "2024", label: "Founded" },
              { metric: "10+", label: "Years Team Experience" },
              { metric: "150+", label: "Brands Supported" },
              { metric: "2", label: "Licensed Facilities" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl lg:text-5xl font-light mb-2 text-brand-teal">{stat.metric}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-secondary p-12 lg:p-16">
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Ready to Partner With Us?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
                Let's discuss how we can support your brand's growth with innovative formulations, flexible manufacturing, and dedicated partnership.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: '#00A2E8' }}
              >
                Start the Conversation
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
